/**
 * **The unit column** (`projects/unit-panel.md` Phase 1): PLAY's left column, the stage's full left
 * edge. The docked command menu of a unit the viewer controls is pinned to its top, the targeting
 * slot (a surface's own targeting panel, `TargetingMode.tsx`) sits under the menu, and the **unit
 * panel** (the selected unit's card, or a hovered unit's as a preview) is pinned to its bottom. Neither
 * moves the other: the menu grows downward (a screen change slides its bottom edge,
 * `useAnimatedHeight`) and the panel grows upward. A hover preview holds the selection's card height
 * and overlays it from the bottom, over the menu's lowest rows when it is taller.
 *
 * The column's top is {@link unitColumnTop}: the stage's top, or just under the round header where
 * the header reaches over the column. When everything will not fit, the menu gives way first and
 * its rows scroll (`token-command-menu.css`).
 *
 * When the panel's unit goes away (a deselect, the pointer leaving a hovered unit with nothing
 * selected), its card stays for {@link PANEL_LEAVE_MS} as an inert leaving copy, fading out where it
 * stood.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { projectForDisplay, type DisplayCombatant, type EncounterState, type ProjectCombatantArgs } from "@lancer-vtt/domain";
import type { UnitSheetIdentity } from "../../../lib/encounter/combatWindows/UnitSheet";
import { UnitIntelCard } from "../../../lib/encounter/UnitIntelCard";
import { UnitPanel } from "../../../lib/encounter/UnitPanel";
import { useAnimatedHeight } from "../../../lib/encounter/useAnimatedHeight";
import { MENU_ENTER_PASSTHROUGH_MS, MENU_LEAVE_MS } from "../../../lib/encounter/CommandMenuPresence";
import "./unit-column.css";

/** A box in the stage's own coordinates. */
export type Box = { left: number; right: number; top: number; bottom: number };

/** The column's inset from the stage's left, bottom and top edges. */
export const COLUMN_EDGE = 16;
/** The gap between the round header's bottom edge and a column that sits under it. */
const HEADER_GAP = 8;
/** How long a gone panel's card stays as it leaves: the menu's own leave. */
export const PANEL_LEAVE_MS = MENU_LEAVE_MS;

/**
 * The column's top edge: {@link COLUMN_EDGE} from the stage's top, or {@link HEADER_GAP} under the
 * round header (top-centre, `.combat-roundheader`) wherever the header overlaps the column's span.
 */
export function unitColumnTop(args: { header: Box | null; columnWidth: number }): number {
  const { header, columnWidth } = args;
  const left = COLUMN_EDGE;
  const underHeader = header !== null && left < header.right && left + columnWidth > header.left;
  return underHeader ? Math.max(COLUMN_EDGE, header.bottom + HEADER_GAP) : COLUMN_EDGE;
}

/** The unit panel's content: the unit, as this viewer may see it. */
export type UnitPanelView = {
  combatant: DisplayCombatant;
  identity: UnitSheetIdentity;
  /** A hovered unit that is not the selection: its card lets the pointer through to the board. */
  hover: boolean;
  /** A hovered unit shown in place of a selection: marked, so it never reads as the menu's unit. */
  preview: boolean;
  /**
   * On a preview, the selection's own view: the panel keeps its height, so the menu above never
   * moves when the pointer crosses a token.
   */
  hold: UnitPanelView | null;
};

/**
 * The unit panel's view (1.3): the hovered unit (`hoverId`, from `useHoverIntelUnit`), else the
 * selection, for every unit kind, through the render-time projection, so it shows only what this
 * viewer may see.
 */
export function unitPanelFor(args: {
  state: EncounterState;
  anchor: { hoverId: string | null; selectedId: string | null };
  viewer: Pick<ProjectCombatantArgs, "viewerSlot" | "role"> & { identityOf: (c: DisplayCombatant) => UnitSheetIdentity };
}): UnitPanelView | null {
  const { state, anchor, viewer } = args;
  const { hoverId, selectedId } = anchor;
  const viewOf = (id: string | null, hover: boolean, preview: boolean): UnitPanelView | null => {
    const unit = id !== null ? state.combatants[id] : undefined;
    if (!unit) return null;
    const combatant = projectForDisplay({ combatant: unit, viewerSlot: viewer.viewerSlot, role: viewer.role, state });
    return { combatant, identity: viewer.identityOf(combatant), hover, preview, hold: null };
  };
  const hover = hoverId !== null && hoverId !== selectedId;
  const preview = hover && selectedId !== null;
  const view = viewOf(hoverId ?? selectedId, hover, preview);
  return view && preview ? { ...view, hold: viewOf(selectedId, false, false) } : view;
}

/** The widest the column gets in Phase 1: the card (the menu matches it, 280). */
const COLUMN_MIN_WIDTH = 280;

export function UnitColumn({
  menu,
  panel,
  slotRef,
  sheet = null,
  onExpand,
  onCollapse = () => {},
}: {
  menu: ReactNode;
  panel: UnitPanelView | null;
  slotRef: (el: HTMLDivElement | null) => void;
  /** The expanded panel's sheet (Phase 2), or `null` while collapsed. */
  sheet?: ReactNode | null;
  /** Grow the selection's panel into the sheet. */
  onExpand?: (() => void) | undefined;
  onCollapse?: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // The menu folding to its strip and back (F1: A) slides like a screen change (1.5). The stack's own
  // slide pins its height before this runs, so a screen change never slides twice.
  const menuRef = useRef<HTMLDivElement>(null);
  useAnimatedHeight(menuRef);
  const [top, setTop] = useState(COLUMN_EDGE);
  // Measured after every render (the round header's content and the column's width both change with
  // the fight) and on a resize of the stage or the header.
  const measure = () => {
    const col = ref.current;
    const stage = col?.offsetParent;
    if (!col || !stage) return;
    const origin = stage.getBoundingClientRect();
    const r = stage.closest(".combat-app")?.querySelector(".combat-roundheader")?.getBoundingClientRect();
    const header = r ? { left: r.left - origin.left, right: r.right - origin.left, top: r.top - origin.top, bottom: r.bottom - origin.top } : null;
    const next = unitColumnTop({ header, columnWidth: Math.max(COLUMN_MIN_WIDTH, col.offsetWidth) });
    setTop((t) => (t === next ? t : next));
  };
  useLayoutEffect(measure);
  useLayoutEffect(() => {
    const stage = ref.current?.offsetParent;
    if (!stage || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(stage);
    const header = stage.closest(".combat-app")?.querySelector(".combat-roundheader");
    if (header) ro.observe(header);
    return () => ro.disconnect();
    // `measure` reads only refs and the DOM.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shown = useLeavingPanel(panel);
  const entering = useEnteringPassthrough(shown && !shown.leaving ? shown.view.combatant.id : null);
  const expanded = sheet !== null && shown !== null && !shown.leaving && !shown.view.hover;
  return (
    <div ref={ref} className="unit-column" style={{ top }} data-testid="unit-column">
      <div className="unit-column-menu" ref={menuRef}>
        {menu}
      </div>
      <div className="tcm-tgt-slot" ref={slotRef} />
      {shown && (
        <div
          className={`unit-column-panel${shown.view.hover ? " is-hover" : ""}${shown.view.hold ? " is-over" : ""}${shown.leaving ? " is-leaving" : ""}${expanded ? " is-expanded" : ""}${entering ? " is-entering" : ""}`}
          data-testid="unit-panel"
          data-expanded={expanded}
          {...(shown.leaving ? { "aria-hidden": true, inert: true } : {})}
        >
          {shown.view.hold && (
            <div className="unit-column-hold" aria-hidden inert>
              <UnitIntelCard combatant={shown.view.hold.combatant} identity={shown.view.hold.identity} onExpand={onExpand} />
            </div>
          )}
          <UnitPanel
            combatant={shown.view.combatant}
            identity={shown.view.identity}
            preview={shown.view.preview}
            sheet={expanded ? sheet : null}
            onExpand={shown.view.hover || shown.leaving ? undefined : onExpand}
            onCollapse={onCollapse}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Whether a panel that just came up for `unitId` still lets the pointer through: for
 * {@link MENU_ENTER_PASSTHROUGH_MS}, like the entering menu (R3), so a double-click on a token the
 * new card covers (the stage's bottom-left) still reaches the token and expands its sheet.
 */
function useEnteringPassthrough(unitId: string | null): boolean {
  const [last, setLast] = useState(unitId);
  const [entering, setEntering] = useState(false);
  if (unitId !== last) {
    setLast(unitId);
    setEntering(unitId !== null);
  }
  useEffect(() => {
    if (!entering) return;
    const t = setTimeout(() => setEntering(false), MENU_ENTER_PASSTHROUGH_MS);
    return () => clearTimeout(t);
  }, [entering, last]);
  return entering;
}

/** The panel to render: the current one, else the last one for {@link PANEL_LEAVE_MS} after it went. */
function useLeavingPanel(panel: UnitPanelView | null): { view: UnitPanelView; leaving: boolean } | null {
  const [last, setLast] = useState<UnitPanelView | null>(panel);
  const [leaving, setLeaving] = useState(false);
  if (panel !== null && (panel !== last || leaving)) {
    setLast(panel);
    setLeaving(false);
  } else if (panel === null && last !== null && !leaving) {
    // Adjusted during render, so the leaving copy is in the very render that drops the panel.
    setLeaving(true);
  }
  useEffect(() => {
    if (!leaving) return;
    const t = setTimeout(() => {
      setLast(null);
      setLeaving(false);
    }, PANEL_LEAVE_MS);
    return () => clearTimeout(t);
  }, [leaving]);
  if (panel) return { view: panel, leaving: false };
  return last ? { view: last, leaving: true } : null;
}
